import type { ClaimIntakeVoiceEventName } from './claimIntakeVoiceAgentConfig';
import type { ContractData } from './claimIntakeUtils';

export type ClaimIntakeVoiceAgentEvent = {
  type: 'event';
  name: string;
  success?: boolean;
  error?: string;
  payload?: Record<string, unknown>;
};

export type ClaimIntakeVoiceContractCandidate = {
  index: number;
  contract_number: string;
  vin: string;
  customer_name: string;
  product_type: string;
};

export type ClaimIntakeVoiceOutboundEvent = {
  type: 'event';
  name: string;
  success: boolean;
  error?: string;
  payload?: Record<string, unknown>;
};

export function isClaimIntakeVoiceEventName(name: string): name is ClaimIntakeVoiceEventName {
  return (
    name === 'fetch_contract' ||
    name === 'select_contract' ||
    name === 'go_to_next_page' ||
    name === 'go_to_repairs_page' ||
    name === 'go_to_previous_page' ||
    name === 'submit_claim'
  );
}

/** Maps agent aliases to canonical event names. */
export function normalizeClaimIntakeVoiceEventName(name: string): ClaimIntakeVoiceEventName | null {
  const n = name.trim().toLowerCase().replaceAll('-', '_');
  if (
    n === 'fetch_contract' ||
    n === 'lookup_contract' ||
    n === 'contract_lookup' ||
    n === 'lookup'
  ) {
    return 'fetch_contract';
  }
  if (n === 'select_contract' || n === 'choose_contract') return 'select_contract';
  if (
    n === 'go_to_repairs_page' ||
    n === 'go_to_third_tab' ||
    n === 'continue_to_form' ||
    n === 'open_repairs' ||
    n === 'go_to_repairs'
  ) {
    return 'go_to_repairs_page';
  }
  if (n === 'go_to_next_page' || n === 'next_page' || n === 'next_step') return 'go_to_next_page';
  if (n === 'go_to_previous_page' || n === 'previous_page' || n === 'prev_page' || n === 'back') {
    return 'go_to_previous_page';
  }
  if (n === 'submit_claim' || n === 'submit') return 'submit_claim';
  return null;
}

export function summarizeContractForVoice(contract: ContractData, index: number): ClaimIntakeVoiceContractCandidate {
  const first = contract.customer?.firstName?.trim() ?? '';
  const last = contract.customer?.lastName?.trim() ?? '';
  const customerName = [first, last].filter(Boolean).join(' ');
  return {
    index,
    contract_number: contract.contractNumber ?? '',
    vin: contract.vehicle?.vin ?? '',
    customer_name: customerName,
    product_type:
      contract.coverage?.productType?.code ?? contract.coverage?.productType?.description ?? '',
  };
}

export function buildContractLookupResultEvent(args: {
  success: boolean;
  status: 'found' | 'multiple' | 'none' | 'error' | 'invalid';
  contracts?: ContractData[];
  selected?: ContractData | null;
  error?: string;
}): ClaimIntakeVoiceOutboundEvent {
  const contracts = args.contracts ?? [];
  const candidates = contracts.map((c, i) => summarizeContractForVoice(c, i));
  const selected =
    args.selected != null ? summarizeContractForVoice(args.selected, 0) : candidates[0] ?? null;

  return {
    type: 'event',
    name: 'contract_lookup_result',
    success: args.success,
    ...(args.error ? { error: args.error } : {}),
    payload: {
      status: args.status,
      count: candidates.length,
      contracts: candidates,
      ...(selected ? { selected } : {}),
      message: (() => {
        if (args.status === 'found') {
          return 'Contract data is fetched. Customer/VIN/contract fields from the contract were sent via form_update — do NOT ask for those again. Ask the caller if they want to continue to fill the claim form. Only if they say yes/continue/move ahead, emit go_to_next_page to open the Repairs form tab. Do not navigate automatically.';
        }
        if (args.status === 'multiple') {
          return 'Multiple contracts found. Ask the caller which contract to select, then emit select_contract.';
        }
        if (args.status === 'none') return 'No contracts found.';
        return args.error ?? 'Contract lookup failed.';
      })(),
      ...(args.status === 'found'
        ? {
            next_action: 'ask_continue_to_form',
            continue_event: 'go_to_next_page',
            continue_step: 2,
            continue_step_label: 'Repairs',
          }
        : {}),
    },
  };
}
